import { useState, useCallback, useEffect, useMemo } from "react";

/**
 * Returns the Monday of the ISO week containing `date`.
 */
const getMondayOf = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0=Sun
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d;
};

/**
 * ISO 8601 week number.
 */
export const getISOWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    // Thursday of same ISO week
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const jan1 = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - jan1) / 86400000 + 1) / 7);
};

const getCardsPerViewport = (width) => {
    if (width > 1400) return 7;
    if (width > 1100) return 4;
    if (width > 768) return 2;
    return 1;
};

/**
 * Hook to manage day-carousel navigation state.
 * Returns everything needed to render the weekday bar and carousel.
 */
const useDayCarousel = () => {
    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    // Initial focused date: snap to Monday if full week fits on screen
    const [focusedDate, setFocusedDate] = useState(() => {
        const initial = getCardsPerViewport(window.innerWidth);
        if (initial >= 7) return getMondayOf(today);
        return today;
    });
    const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
    const [selectedYear, setSelectedYear] = useState(today.getFullYear());
    const [cardsPerViewport, setCardsPerViewport] = useState(() => getCardsPerViewport(window.innerWidth));

    // Track window width for responsive card count
    useEffect(() => {
        const handleResize = () => {
            setCardsPerViewport(getCardsPerViewport(window.innerWidth));
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // effectiveStartDate is just focusedDate — initial Monday snap is
    // handled by the useState initializer above.
    const effectiveStartDate = focusedDate;

    // Week days for the focused date's week
    const currentWeekDays = useMemo(() => {
        const monday = getMondayOf(focusedDate);
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            return d;
        });
    }, [focusedDate]);

    // Index of focusedDate within its week (0=Mon, 6=Sun)
    const focusedWeekdayIndex = useMemo(() => {
        const monday = getMondayOf(focusedDate);
        return Math.round((focusedDate - monday) / 86400000);
    }, [focusedDate]);

    // Compute weekOffset relative to today (for useDashboard compatibility)
    const weekOffset = useMemo(() => {
        const todayMonday = getMondayOf(today);
        const focusedMonday = getMondayOf(focusedDate);
        return Math.round((focusedMonday - todayMonday) / (7 * 86400000));
    }, [today, focusedDate]);

    const goToDate = useCallback((date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        setFocusedDate(d);
        setSelectedMonth(d.getMonth());
        setSelectedYear(d.getFullYear());
    }, []);

    const goToToday = useCallback(() => {
        const cards = getCardsPerViewport(window.innerWidth);
        if (cards >= 7) {
            // Full week: snap to Monday
            goToDate(getMondayOf(today));
        } else {
            // Center today in the visible cards
            const offset = Math.floor((cards - 1) / 2);
            const centered = new Date(today);
            centered.setDate(centered.getDate() - offset);
            goToDate(centered);
        }
    }, [today, goToDate]);

    const goToNext = useCallback(() => {
        setFocusedDate((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() + 1);
            setSelectedMonth(d.getMonth());
            setSelectedYear(d.getFullYear());
            return d;
        });
    }, []);

    const goToPrev = useCallback(() => {
        setFocusedDate((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() - 1);
            setSelectedMonth(d.getMonth());
            setSelectedYear(d.getFullYear());
            return d;
        });
    }, []);

    const goToNextWeek = useCallback(() => {
        setFocusedDate((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() + 7);
            setSelectedMonth(d.getMonth());
            setSelectedYear(d.getFullYear());
            return d;
        });
    }, []);

    const goToPrevWeek = useCallback(() => {
        setFocusedDate((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() - 7);
            setSelectedMonth(d.getMonth());
            setSelectedYear(d.getFullYear());
            return d;
        });
    }, []);

    const handleMonthYearChange = useCallback((month, year) => {
        setSelectedMonth(month);
        setSelectedYear(year);
        const target = new Date(year, month, 1);
        target.setHours(0, 0, 0, 0);
        setFocusedDate(target);
    }, []);

    return {
        focusedDate,
        effectiveStartDate,
        selectedMonth,
        selectedYear,
        cardsPerViewport,
        currentWeekDays,
        focusedWeekdayIndex,
        weekOffset,
        goToDate,
        goToToday,
        goToNext,
        goToPrev,
        goToNextWeek,
        goToPrevWeek,
        handleMonthYearChange,
    };
};

export default useDayCarousel;
